import { ModalTypes } from "@lib/enums/ModalTypes";
import type { SiteSettings } from "@lib/contentful/types";
import dynamic from "next/dynamic";

export interface IAuthorizationModules {
    offerToLogIn: SiteSettings["auth"]["offerToLogIn"];
    offerToCreateAnAccount: SiteSettings["auth"]["offerToCreateAnAccount"];
    policiesAgreementTerms: SiteSettings["auth"]["policiesAgreementTerms"];
    authorizationInvitation: SiteSettings["auth"]["authorizationInvitation"];
    forgotYourPassword: SiteSettings["auth"]["forgotYourPassword"];
    eccoNewsletter: SiteSettings["auth"]["newsletter"];
    eccoMembership: SiteSettings["auth"]["membership"];
}

export interface IContactFormModules {
    reCaptchaAgreement: SiteSettings["legal"]["reCaptchaAgreement"];
}

const LogInModal = dynamic(() => import("@components/auth/login/LogInModal"), { ssr: false });
const SignUpModal = dynamic(() => import("@components/auth/sign-up/SignUpModal"), { ssr: false });
const LoggedOutModal = dynamic(
    () => import("@components/ui/modals/logged-out-modal/LoggedOutModal"),
    { ssr: false }
);
const ThankYouForSignUpModal = dynamic(
    () => import("@components/ui/modals/thank-you-modal/ThankYouForSignUpModal"),
    { ssr: false }
);
const SizeGuideModal = dynamic(() => import("@components/ui/size-guide-modal/SizeGuideModal"), {
    ssr: false,
});
const GlobalSizeGuideModal = dynamic(
    () =>
        import("@components/ui/size-guide-modal/GlobalSizeGuideModal").then(
            (mod) => mod.GlobalSizeGuideModal
        ),
    { ssr: false }
);
const ShippingDetails = dynamic(
    () => import("@features/checkout/components/delivery-step/ShippingDetailsModal"),
    { ssr: false }
);
const BaseModal = dynamic(() => import("@components/ui/modals/base-modal/BaseModal"), {
    ssr: false,
});
const GiftWrappingModal = dynamic(
    () => import("@components/ui/modals/gift-wrapping/GiftWrappingModal"),
    { ssr: false }
);
const AddedToWishlistModal = dynamic(
    () => import("@components/ui/modals/add-to-wishlist-modal/AddedToWishlistModal"),
    { ssr: false }
);

const ContactForm = dynamic(
    () => import("@components/ui/modals/contact-form/ContactForm").then((mod) => mod.ContactForm),
    { ssr: false }
);
const ContactFormSubmitted = dynamic(
    () =>
        import("@components/ui/modals/contact-form/ContactFormSubmitted").then(
            (mod) => mod.ContactFormSubmitted
        ),
    { ssr: false }
);
const ForgotPassword = dynamic(() => import("../auth/login/ForgotPassword"));
const upgradeAccountModal = dynamic(() => import("@components/auth/loyalty/UpgradeAccountModal"));
const EccoPassModal = dynamic(() => import("@components/ui/modals/ecco-club/EccoPassModal"));
const EccoClubBalanceHistoryModal = dynamic(
    () => import("@components/ui/modals/ecco-club/BalanceHistoryModal")
);
const AccountDeactivationModal = dynamic(
    () => import("@components/ui/modals/account-deactivation/AccountDeactivationModal")
);
const LegalModal = dynamic(() => import("@components/ui/modals/legal-modal/LegalModal"), {
    ssr: false,
});
const ReturnLabelModal = dynamic(
    () => import("@components/ui/modals/return-label/ReturnLabelModal"),
    { ssr: false }
);
const NewsletterSignupModal = dynamic(
    () => import("@components/newsletter-subscription/NewsletterSignupModal"),
    { ssr: false }
);
type ModalMap = {
    [key in ModalTypes]?: React.ComponentType<any>;
};

export const modalMap: ModalMap = {
    [ModalTypes.login]: LogInModal,
    [ModalTypes.signOut]: LoggedOutModal,
    [ModalTypes.sizeGuide]: SizeGuideModal,
    [ModalTypes.globalSizeGuide]: GlobalSizeGuideModal,
    [ModalTypes.createAccount]: SignUpModal,
    [ModalTypes.thankYouForSignUp]: ThankYouForSignUpModal,
    [ModalTypes.forgotPassword]: ForgotPassword,
    [ModalTypes.contactForm]: ContactForm,
    [ModalTypes.contactFormSubmitted]: ContactFormSubmitted,
    [ModalTypes.shippingDetails]: ShippingDetails,
    [ModalTypes.giftWrapping]: GiftWrappingModal,
    [ModalTypes.addToWishList]: AddedToWishlistModal,
    [ModalTypes.base]: BaseModal,
    [ModalTypes.upgradeAccount]: upgradeAccountModal,
    [ModalTypes.eccoPass]: EccoPassModal,
    [ModalTypes.eccoClubBalanceHistory]: EccoClubBalanceHistoryModal,
    [ModalTypes.accountDeactivation]: AccountDeactivationModal,
    [ModalTypes.legal]: LegalModal,
    [ModalTypes.returnLabel]: ReturnLabelModal,
    [ModalTypes.newsletterSignup]: NewsletterSignupModal,
};
